import { useParams, useNavigate } from 'react-router-dom'
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material'
import { Description, Download, ArrowBack, AttachFile, Upload } from '@mui/icons-material'
import { useGetExamInfoQuery } from '../store/api/casesApi'
import { PageHeader } from '../components/PageHeader'
import { getContentPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'
import { API_BASE_URL } from '../config/api'
import { useAppSelector } from '../hooks/redux'
import { hasPermission } from '../utils/permissions'

/**
 * UAT-TC #7: Đề thi đầy đủ — tóm tắt + danh mục tài liệu + yêu cầu bài nộp.
 */
export default function ExamInfoPage() {
  const { caseId = '' } = useParams<{ caseId: string }>()
  const navigate = useNavigate()
  const { data, isLoading, error } = useGetExamInfoQuery(caseId, { skip: !caseId })
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const canSubmitMemorial = !!currentUser && hasPermission(currentUser.role, 'SUBMIT_MEMORIAL')
  const canUploadEvidence = !!currentUser && hasPermission(currentUser.role, 'UPLOAD_EVIDENCE')

  const handleDownloadExamPaper = () => {
    const token = localStorage.getItem('access_token') || ''
    const url = `${API_BASE_URL}/cases/${caseId}/exam-paper`
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = u
        a.download = `exam-paper-${caseId}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(u)
      })
      .catch(() => alert('Không tải được đề thi'))
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Container>
    )
  }

  if (error || !data) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Alert severity="error">
          Không thể tải đề thi. Có thể đội bạn chưa được duyệt hoặc cuộc thi chưa cấp đề.
        </Alert>
        <Button startIcon={<ArrowBack />} sx={{ mt: 2 }} onClick={() => navigate('/cases')}>
          Quay về danh sách cuộc thi
        </Button>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
      <PageHeader
        title={`Đề thi: ${data.title}`}
        subtitle={data.case_number ? `Số: ${data.case_number}` : undefined}
        icon={<Description />}
        actions={
          <Button startIcon={<ArrowBack />} variant="outlined" onClick={() => navigate('/cases')}>
            Quay lại
          </Button>
        }
      />

      {/* Section 1: Tóm tắt vụ án */}
      <Paper elevation={0} sx={(t) => ({ ...getContentPaperSx(t), mb: 3 })}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          1. Tóm tắt vụ án
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {data.case_type && <Chip label={`Loại: ${data.case_type}`} size="small" />}
          {data.legal_area && <Chip label={`Lĩnh vực: ${data.legal_area}`} size="small" />}
          {data.dispute_value && <Chip label={`Giá trị: ${data.dispute_value}`} size="small" color="warning" />}
        </Box>
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
          {data.summary || 'Chưa có tóm tắt.'}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Nguyên đơn</Typography>
            <Typography variant="body2" fontWeight={500}>{data.plaintiff || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Bị đơn</Typography>
            <Typography variant="body2" fontWeight={500}>{data.defendant || '—'}</Typography>
          </Box>
        </Box>
        {data.parties_requests && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">Yêu cầu các bên</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {data.parties_requests}
            </Typography>
          </>
        )}
      </Paper>

      {/* Section 2: Đề bài + Pool chứng cứ */}
      <Paper elevation={0} sx={(t) => ({ ...getContentPaperSx(t), mb: 3 })}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            2. Đề bài & Danh mục chứng cứ
          </Typography>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownloadExamPaper}
            sx={{ textTransform: 'none' }}
          >
            Tải đề thi
          </Button>
        </Box>

        {/* 2a — Đề bài & Tài liệu nền */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'text.secondary' }}>
          Đề bài & Tài liệu nền ({data.materials.length})
        </Typography>
        {data.materials.length > 0 ? (
          <TableContainer component={Paper} variant="outlined" sx={(t) => ({ ...getTablePaperSx(t), mb: 2 })}>
            <Table size="small" sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Tài liệu</strong></TableCell>
                  <TableCell sx={{ width: 110 }}><strong>Định dạng</strong></TableCell>
                  <TableCell sx={{ width: 110 }}><strong>Kích thước</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.materials.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell>{m.title || m.file_name}</TableCell>
                    <TableCell>{(m.mime_type || '').split('/')[1] || m.mime_type}</TableCell>
                    <TableCell>{(m.file_size / 1024).toFixed(1)} KB</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Chưa có tài liệu nền nào. Liên hệ BTC nếu thiếu.
          </Alert>
        )}

        {/* 2b — Pool Chứng cứ (đội chọn ở /evidence) */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, mt: 2, color: 'text.secondary' }}>
          Danh mục chứng cứ
        </Typography>
        {data.evidence_pool_count && data.evidence_pool_count > 0 ? (
          <Alert
            severity="info"
            action={
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(`/evidence?caseId=${caseId}`)}
                sx={{ textTransform: 'none' }}
              >
                Mở pool chứng cứ →
              </Button>
            }
          >
            BTC cung cấp <strong>{data.evidence_pool_count} tài liệu</strong> trong pool — đội thi vào trang
            <strong> Chứng cứ</strong> để chọn các file đội tin là chứng cứ phù hợp. Quy tắc Vòng Sơ loại:
            chỉ chọn từ pool, không upload file ngoài.
          </Alert>
        ) : (
          <Alert severity="warning">
            BTC chưa nạp pool chứng cứ cho cuộc thi này. Liên hệ BTC nếu thiếu.
          </Alert>
        )}
      </Paper>

      {/* Section 3: Yêu cầu bài nộp */}
      <Paper elevation={0} sx={(t) => getContentPaperSx(t)}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          3. Yêu cầu bài nộp
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Đây là quy định bắt buộc do BTC đặt ra <strong>cho riêng đề thi này</strong>.
          Đội thi cần đọc kỹ và tuân thủ khi nộp Đơn khởi kiện (tại trang <em>/memorials</em>) và
          Chứng cứ (tại trang <em>/evidence</em>). Bài nộp sai quy cách có thể bị trừ điểm
          hoặc không được chấm.
        </Typography>
        {data.submission_requirements ? (
          <Alert severity="warning" icon={false} sx={{ alignItems: 'flex-start' }}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                lineHeight: 1.7,
              }}
            >
              {data.submission_requirements}
            </Typography>
          </Alert>
        ) : (
          <Alert severity="info">
            BTC chưa cập nhật yêu cầu bài nộp cho đề thi này. Vui lòng theo dõi thông báo
            hoặc liên hệ BTC trước khi nộp bài.
          </Alert>
        )}

        {(canSubmitMemorial || canUploadEvidence) && (
          <Box
            sx={{
              mt: 3,
              pt: 3,
              borderTop: '1px dashed',
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>
              Sẵn sàng nộp bài?
            </Typography>
            {canSubmitMemorial && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Upload />}
                onClick={() => navigate(`/memorials?caseId=${caseId}`)}
                sx={{ textTransform: 'none' }}
              >
                Nộp Đơn khởi kiện
              </Button>
            )}
            {canUploadEvidence && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AttachFile />}
                onClick={() => navigate(`/evidence?caseId=${caseId}`)}
                sx={{ textTransform: 'none' }}
              >
                Nộp Chứng cứ
              </Button>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              Hệ thống tự động chọn đề thi này khi mở trang nộp bài.
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  )
}
